import React, {useContext, useEffect, useState} from 'react';
import JobListViewContext, { RightClickContext } from '../common/JobListViewContext';
import { JobNames } from '../../framework.core/JobNames';
import { ActionButtonItem, borderColors, splitMenuItemsIntoSections } from '../../ui/ActionButton';
import ToolboxMenuItem from '../../uiControls/ToolboxMenuItem';
import ListViewShortcutMenu from '../common/ListViewShortcutMenu';
import useFunctionKeys from '../../hooks/useFunctionKeys';
import InfoAreaDrawer from '../../uiControls/infoArea/InfoAreaDrawer';
import AbstractListView, { DataFetchResult, ExportRowRenderer, ListCustomFilterRenderer, ListOrderBy, ListRowRenderer } from '../common/AbstractListView';
import { Search } from '../../filtering';
import constants from '../../constants';
import {  NullableCheckboxIconRenderer, TextAlignLeftRenderer } from '../../uiControls/SmartTableRenderers';
import { TableColumn, TableSettings, utEmailTemplateList } from '../../tableSettings';
import JobListSuntekCommand from '../common/JobListSuntekCommand';
import Menu, { IfmsMenuItem } from '../../ui/menu/Menu';
import { MemoOpTypeRenderer } from '../common/commonListRenderers';
import { useHistory } from 'react-router';
import { NavigationType, openNewPage } from '../../util/navigationHelpers';
import ApplicationContext from '../../ApplicationContext';
import { useSecureNavigate } from '../../hooks/useSecureNavigate';
import useGlobalOkDialog from '../../hooks/useGlobalOkDialog';
import { EmailTemplateListItem } from '../../fms.data/models/EmailTemplateListItem';
import useGlobalYesNoDialog from '../../hooks/useGlobalYesNoDialog';
import UniqueLinkButton from '../../uiControls/UniqueLinkButton';
import { DeleteIconButton2, EditIconButton2, ViewIconButton2 } from '../../ui/IconButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import * as XCol from '../../framework.core/XExportColumn';

function useSelectedOperations() {
  const jobListContext = useContext(JobListViewContext);
  const appContext = useContext(ApplicationContext);
  const history = useHistory();

   const selectedElement = jobListContext.selectedElement as EmailTemplateListItem;

   const [showGlobalOkDialog] = useGlobalOkDialog();
   const [showGlobalYesNoDialog] = useGlobalYesNoDialog();

   async function create() {
     if (jobListContext.hasOpenedScreenStartingWithUrl(`/utility/emailTemplate/add`)) {
       await showGlobalOkDialog(<p>New operation is already in progress</p>);
       return;
     }

     openNewPage(history, `/utility/emailTemplate/add`, NavigationType.newPage);
   }

   async function deleteEntity() {
     if (!selectedElement) {
       return;
     }
  
     const doDelete = await showGlobalYesNoDialog(
       <div>
         <h3>Confirm to Delete?</h3>
       </div>
     );

     if (doDelete) {
       const apiResult = await appContext.api.deleteJobDetail(JobNames.EmailTemplate, {
         id: selectedElement.id,
       });
     if (apiResult && apiResult.success) {
         jobListContext.reloadData();
       } else {
         const message = apiResult?.message ?? 'Cannot delete!';
         await showGlobalOkDialog(message);
       }
     }
   }

   async function edit() {
     if (!selectedElement) {
       return;
     }

     if (jobListContext.hasOpenedScreenStartingWithUrl(`/utility/emailTemplate/edit`)) {
       await showGlobalOkDialog(<p>Edit operation is already in progress</p>);
       return;
     }

     const id = selectedElement.id;
     openNewPage(history, `/utility/emailTemplate/edit/${id}`, NavigationType.newPage);
   }

   async function view() {
     if (!selectedElement) {
       return;
     }

     const id = selectedElement.id;

     const viewUrl = `/utility/emailTemplate/view/${id}`;

     if (jobListContext.hasOpenedScreenStartingWithUrl(viewUrl)) {
       await showGlobalOkDialog(<p>View operation is already in progress</p>);
       return;
     }

     openNewPage(history, viewUrl, NavigationType.newPage);
  }



  return {create, edit, deleteEntity, view};
}


function RightClickMenu({dismiss}:{dismiss: VoidFunction}) {
  const { create, deleteEntity, edit, view } = useSelectedOperations();
  const jobListContext = useContext(JobListViewContext);
  const items = [
    { label: 'New', shortcut: 'F2', separator: false,  },
    { label: 'View', shortcut: 'F9', separator: false },
    { label: 'Edit', shortcut: 'F3', separator: false },
    { label: 'Delete', shortcut: '', separator: true},
    { label: 'Search', shortcut: 'F5', separator: false },
    { label: 'Refresh', shortcut: 'F11', separator: true },
  ];

  const menuItems: ActionButtonItem[] = items.map((i, index) =>({
    label: <ToolboxMenuItem width="190px" label={i.label} shortcut={i.shortcut} />,
    separator: i.separator,
    selected: false,
    index,
  }));

  function onMenuItemSelected(item:IfmsMenuItem) {
    switch (item.index) {
      case 0:
        create();
        break;
      case 1:
        view();
        break;
      case 2:
        edit();
        break;
      case 3:
        deleteEntity();
        break;
      case 4:
        jobListContext.triggerTableSearch('F5');
        break;
      case 5:
        jobListContext.reloadData();
        break;
      default:
        break;
    }
    dismiss();
  }

  return (
    <Menu
      variant="plain"
      specialMenu='rightClickContext'
      items={splitMenuItemsIntoSections(menuItems)}
      mode="vertical"
      onMenuItemSelected={onMenuItemSelected}
      subMenuStyle={{
        minWidth: 150,
        whiteSpace: 'nowrap',
      }}
      menuStyle={(i: number) => ({
        maxHeight: '300px',
        overflowY: 'hidden',
        padding: 0,
        borderTop: `3px solid ${borderColors[i % borderColors.length]}`,
        paddingTop: 10,
      })}
      dismiss={dismiss}
    />
  );
}

function ListCommands() {
  const jobListContext = useContext(JobListViewContext);
  const {deleteEntity, edit, view,} = useSelectedOperations();

  return (
        <div className='ifms-common-no-vertical-separator-gap-margin'>
         <div className='ifms-common-vertical-gap-8'>
          <UniqueLinkButton newStyle name='bankRecon-list-add-button' display="primary" link={`/utility/emailTemplate/add`} label={<FontAwesomeIcon icon={faPlus}/>} useOnlyIcon data-tooltip='New' iconMode />
          <EditIconButton2 name='bankRecon-list-edit-button' onClick={edit} useText/>
          <ViewIconButton2 name='bankRecon-list-view-button'  onClick={view} useText/>   
          <DeleteIconButton2 name='gBill-list-delete-button' onClick={deleteEntity} useText />
          </div>
        </div>
  );
}

function ListToolbar() {
  return (
    <>
      <ListViewShortcutMenu />
    </>
  );
}

function ListViewExtras() {
  useFunctionKeys({onFunction: onFunctionKey});

  const jobListContext = useContext(JobListViewContext);

  const {create, edit, view} = useSelectedOperations();

  const [unprocessedFkey, setUnprocessedFkey] = useState<string | null>(null);

  useEffect(() => {
      if (!unprocessedFkey) {
        return;
      }

      doProcessFkey();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [unprocessedFkey]
  );

  function doProcessFkey() {
    if (unprocessedFkey === "F2") {
        create();
    } else if (unprocessedFkey === "F3") {
        edit();
    } else if (unprocessedFkey === "F9") {
      view();
    } else if (unprocessedFkey === "F11") {
      jobListContext.reloadData();
    } 
    setUnprocessedFkey(null);
  }
  function onFunctionKey(fkey: string) {
    if (['F2','F3','F9','F11'].indexOf(fkey) >= 0) {
      setUnprocessedFkey(fkey);
      return true;
    }

    return false;
  }

  return (
    <InfoAreaDrawer
      showContent={jobListContext.infoAreaContent}
      jobName={JobNames.EmailTemplate}
      jobId={(jobListContext.selectedElement as EmailTemplateListItem | null)?.id ?? 0}
      onClose={() => jobListContext.setInfoAreaContent('none')}
    />
  );
}

/**
 *
 * @class EmailTemplateListView
 * @extends {AbstractListView}
 */
class EmailTemplateListView extends AbstractListView {
  async fetchAndFormatData(api: any, search: Search, pageNum: number, limit: number): Promise<DataFetchResult> {
    const apiResult = await api.getListItems(
      JobNames.EmailTemplate,
      'EmailTemplateListItem',
      EmailTemplateListItem.calculateSearchParameters(search,EmailTemplateListItem.MapSearchFields),
      pageNum,
      limit,
      search.pageSize,
      search.pagePerBatch,
    );

    if (!apiResult || !apiResult.entities || apiResult.entities.length <= 0) {
      return {data: [], totalCount: 0};
    }

    return {data: EmailTemplateListItem.formatData(apiResult.entities), totalCount: apiResult.totalCount};
  }


  async exportFormatDataWithExcel(api: any, search: Search, pageNum: number, limit: number, pageSize: number, pagePerBatch: number): Promise<Array<any>> {
    const apiResult = await api.getListItems(JobNames.EmailTemplate, 'EmailTemplateListItem',
      EmailTemplateListItem.calculateSearchParameters(search, EmailTemplateListItem.MapSearchFields),
      pageNum,
      limit,
      pageSize,
      pagePerBatch,
    );

    if (!apiResult || !apiResult.entities || apiResult.entities.length <= 0) {
      return [];
    }

    return  EmailTemplateListItem.formatData(apiResult.entities);
  }

  getCustomFilterRenderers(): ListCustomFilterRenderer[] {
    return [
      { 
        accessor: 'opType',
        renderer: MemoOpTypeRenderer,
        searchValueExtractor: (v:any|null|undefined) => v,
      }
    ];
  }

  getInfoAreaEntityId() {
    return constants.infoAreaEntity.oceanImportMbl;
  }

  getRowRenderers(): ListRowRenderer[] {
    return EmailTemplateListItem.getExportCollection().map(x => {
      const colRenderer:ListRowRenderer = { accessor: x.dataIndex, Cell: TextAlignLeftRenderer };
      if (x.type === XCol.XCellType.Bool || x.type === XCol.XCellType.StrBool) colRenderer.Cell = NullableCheckboxIconRenderer;

      return colRenderer;
    });
  }
  
  getExportRenderers(): ExportRowRenderer[] {
    const colRenderer = [];
    colRenderer.push({accessor: 'status', changedValue: (value: any)=> { return value['status'] === 'PC' ? 'Period Close' : ''}});
    return colRenderer;
  }

  getDefaultOrderBy(): ListOrderBy {
    return {
      orderBy: 'id',
      orderByAscending: false,
    };
  }

  getTableSettings(): TableSettings {
    const tsettings =  utEmailTemplateList;
    const cols = EmailTemplateListItem.getExportCollection();
    tsettings.columns = cols.map<TableColumn>(x => {
      return { ...x, accessor: x.dataIndex, Header: x.text, filterType: undefined, filterTypeV2: x.type};
    });
    return tsettings;
  }

  renderCommands() {
    return (
      <ListCommands/>
    );
  }

  renderBeforeConfigureButton() {
    return (
      <ListToolbar/>
    );
  }

  
  hasMemo(): boolean {
    return false;
  }
  renderExtras() {
    return <ListViewExtras/>
  }

  getViewUrlFor(row: EmailTemplateListItem) {
    return row && row.id ? `/utility/emailTemplate/view/${row.id}` : '';
  }

  getListViewTitle() {
    return 'Email Template List';
  };

  renderAdditionalCommands() {
    return <JobListSuntekCommand jobName={JobNames.EmailTemplate} keyProp='id' showAdjustBalance={false} />;
  }

  hasRightClickContext(): boolean {
    return true;
  }

  renderRightClickContext(context: RightClickContext, dismiss: VoidFunction): React.ReactNode {
    return <RightClickMenu dismiss={dismiss} />;
  }
}

export default EmailTemplateListView;